import { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import Drawer from "react-modern-drawer";
import "react-modern-drawer/dist/index.css";
import { X } from "lucide-react";
import { RazorInputField } from "../../../../components/razor-reuse";
import { Button } from "../../../../components/custom/custom-button";
import type { AppDispatch } from "../../../../redux/store";
import RootState from "../../../../redux/type";
import { createNewRateAlert, updateExistingRateAlert } from "../../../../redux/rateIntelligence";
import { ALERT_COMPETITOR_OPTIONS, RateAlertMode, RateAlertRule } from "../types";

interface Option {
  label: string;
  value: string;
}

// NOTIFY_WHEN_OPTIONS originally had a third choice ("No fresh quote for")
// that mapped to a mode the live API doesn't support — only `rate_change`
// and `behind_only` exist server-side, so that option is dropped here.
// "Ahead of SmartSend by %" and `behind_only` describe the same condition
// from opposite sides (a competitor ahead of SmartSend = SmartSend behind),
// so the original label is kept as-is.
const NOTIFY_WHEN_OPTIONS: { label: string; value: RateAlertMode }[] = [
  { label: "Rate changes", value: "rate_change" },
  { label: "Ahead of SmartSend by %", value: "behind_only" },
];

const EMPTY_NAME = "";
const DEFAULT_MIN_CHANGE_PERCENT = "0.5";

interface CreateAlertDrawerProps {
  open: boolean;
  onClose: (saved?: boolean) => void;
  editingRule?: RateAlertRule | null;
}

export default function CreateAlertDrawer({ open, onClose, editingRule }: CreateAlertDrawerProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { comparison_currency_pair_options } = useSelector(
    (state: RootState) => state.rate_intelligence,
  );
  const currencyPairOptions: Option[] = comparison_currency_pair_options || [];

  const [name, setName] = useState(EMPTY_NAME);
  const [currencyPairs, setCurrencyPairs] = useState<Option[]>([]);
  const [competitors, setCompetitors] = useState<Option[]>([]);
  const [notifyWhen, setNotifyWhen] = useState(NOTIFY_WHEN_OPTIONS[0]);
  const [threshold, setThreshold] = useState(DEFAULT_MIN_CHANGE_PERCENT);
  const [creating, setCreating] = useState(false);

  const isEditing = !!editingRule;

  const resetForm = () => {
    setName(EMPTY_NAME);
    setCurrencyPairs([]);
    setCompetitors([]);
    setNotifyWhen(NOTIFY_WHEN_OPTIONS[0]);
    setThreshold(DEFAULT_MIN_CHANGE_PERCENT);
  };

  useEffect(() => {
    if (!open) return;

    if (editingRule) {
      setName(editingRule.name || EMPTY_NAME);
      setCurrencyPairs(
        editingRule.currency_pairs
          ? currencyPairOptions.filter((o) => editingRule.currency_pairs?.includes(o.value))
          : [],
      );
      setCompetitors(
        editingRule.competitors
          ? ALERT_COMPETITOR_OPTIONS.filter((o) => editingRule.competitors?.includes(o.value as any))
          : [],
      );
      setNotifyWhen(
        NOTIFY_WHEN_OPTIONS.find((o) => o.value === editingRule.mode) || NOTIFY_WHEN_OPTIONS[0],
      );
      setThreshold(String(editingRule.min_change_percent));
    } else {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingRule]);

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    setCreating(true);
    try {
      const payload = {
        name: name.trim() || null,
        mode: notifyWhen.value,
        min_change_percent: Number(threshold) || 0,
        currency_pairs: currencyPairs.length > 0 ? currencyPairs.map((o) => o.value) : null,
        competitors: competitors.length > 0 ? (competitors.map((o) => o.value) as any) : null,
      };
      await dispatch(
        (isEditing
          ? updateExistingRateAlert({ ...payload, id: editingRule.id })
          : createNewRateAlert(payload)) as any,
      ).unwrap();
      resetForm();
      onClose(true);
    } catch (error) {
      console.error(`Failed to ${isEditing ? "update" : "create"} alert:`, error);
    } finally {
      setCreating(false);
    }
  };

  return ReactDOM.createPortal(
    <Drawer open={open} onClose={handleCancel} direction="right" className="bg-white" size="420px">
      <div className="flex items-center justify-between px-[24px] py-[20px] border-b border-[#E4E4E7]">
        <p className="text-[18px] font-bold text-[#1E1E1E]">
          {isEditing ? "Edit alert" : "Create alert"}
        </p>
        <button
          type="button"
          onClick={handleCancel}
          className="text-[#A1A1AA] hover:text-[#1E1E1E]"
        >
          <X className="size-[18px]" />
        </button>
      </div>

      <div className="p-[24px] space-y-[20px]">
        <div>
          <label className="text-[13px] font-medium text-[#1E1E1E]">Name (optional)</label>
          <input
            type="text"
            maxLength={255}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Wise falling behind on GBP"
            className="mt-[8px] h-[40px] w-full rounded-[8px] border border-[#E4E4E7] px-[12px] text-[14px] text-[#1E1E1E] outline-none focus:border-[#734792]"
          />
        </div>

        <RazorInputField
          color="black-light"
          type="select"
          multi
          label="Currency pairs"
          placeholder="All pairs"
          value={currencyPairs}
          onChange={(selected: any) => setCurrencyPairs(selected || [])}
          selectOption={currencyPairOptions}
        />

        <RazorInputField
          color="black-light"
          type="select"
          multi
          label="Providers"
          placeholder="All competitors"
          value={competitors}
          onChange={(selected: any) => setCompetitors(selected || [])}
          selectOption={ALERT_COMPETITOR_OPTIONS}
        />

        <RazorInputField
          color="black-light"
          type="select"
          label="Notify me when"
          value={notifyWhen}
          onChange={(e: any) => setNotifyWhen(e)}
          selectOption={NOTIFY_WHEN_OPTIONS}
        />
        <div>
          <label className="text-[13px] font-medium text-[#1E1E1E]">Minimum change (%)</label>
          <input
            type="number"
            min={0.01}
            max={100}
            step={0.01}
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
            className="mt-[8px] h-[40px] w-full rounded-[8px] border border-[#E4E4E7] px-[12px] text-[14px] text-[#1E1E1E] outline-none focus:border-[#734792]"
          />
          <p className="mt-[6px] text-[12px] text-[#A1A1AA]">Between 0.01% and 100%.</p>
        </div>
      </div>

      <div className="flex items-center justify-end gap-[12px] px-[24px] py-[20px] border-t border-[#E4E4E7]">
        <Button variant="primary-outline" onClick={handleCancel}>
          Cancel
        </Button>
        <Button isLoading={creating} onClick={handleSubmit}>
          {isEditing ? "Save changes" : "Create alert"}
        </Button>
      </div>
    </Drawer>,
    document.body,
  );
}
